import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { BusinessProfileResponse } from 'apis/business/types';
import { queryClient } from 'providers/AllProviders';
import { useParams } from 'react-router-dom';
import {
  createBusiness,
  getBusiness,
  getAgencyAgents,
  getBusinesses,
  getDeleteBusiness,
  resetBusiness,
  updateBusiness
} from './api';

export function useGetBusinesses(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: BusinessProfileResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: BusinessProfileResponse[]; meta: MetaApiResponse }>({
    queryKey: ['businesses', params],
    queryFn: () => getBusinesses(params),
    ...options
  });
}

export function useGetBusiness(id: number, params?: Record<string, any>, options?: any) {
  return useQuery<BusinessProfileResponse>({
    queryKey: ['agency_business', id, params],
    queryFn: () => getBusiness(id, params),
    enabled: Boolean(id),
    ...options
  });
}

export function useDeleteBusiness() {
  return useMutation({
    mutationFn: (id: number) => getDeleteBusiness(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['businesses'],
        refetchType: 'active'
      });
    }
  });
}

export function useCreateBusiness(handleClose: () => void) {
  return useMutation({
    mutationFn: (payload: FormData) => createBusiness(payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.refetchQueries({
          queryKey: ['businesses']
        });
        handleClose();
      }
    }
  });
}

export function useUpdateBusiness(handleClose: () => void) {
  const { businessId } = useParams<{ businessId: string }>();
  return useMutation({
    mutationFn: (payload: FormData) => updateBusiness(Number(businessId), payload),
    onSuccess: async (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['businesses']
        });
        queryClient.invalidateQueries({
          queryKey: ['businessProfile'],
          refetchType: 'active'
        });
        queryClient.invalidateQueries({
          queryKey: ['agency_business'],
          refetchType: 'active'
        });
        handleClose();
      }
    }
  });
}

export function useRestBusiness() {
  return useMutation({
    mutationFn: (id: number) => resetBusiness(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['businesses']
      });
    }
  });
}

export function useGetAgencyAgents(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: any[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: any[]; meta: MetaApiResponse }>({
    queryKey: ['agency_agents', params],
    queryFn: () => getAgencyAgents(params),
    ...options
  });
}
